import type { ReactNode } from "react";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import { useSEO } from "../hooks/useSEO";

/* -------------------------
   Inline icons
   ------------------------- */

const QrIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20v1" />
  </svg>
);

const IdIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="9" cy="12" r="2.5" />
    <path d="M14 10h4M14 14h4M5.5 18c.5-1.7 2-2.6 3.5-2.6s3 .9 3.5 2.6" />
  </svg>
);

const ShieldIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
  >
    <path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3z" />
    <path d="M9.5 12.5l2 2 3.5-3.5" />
  </svg>
);

const WaveIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
  >
    <path d="M4 12v0M8 8v8M12 5v14M16 8v8M20 12v0" />
  </svg>
);

/* -------------------------
   Step Card
   ------------------------- */

type StepCardProps = {
  number: string;
  title: string;
  description: string;
  bullets: string[];
};

const StepCard = ({
  number,
  title,
  description,
  bullets,
}: StepCardProps) => (
  <article
    className="
      hiw-step-card
      flex-1
      min-w-0
      bg-white
      border
      border-black/60
      rounded-[10px]
      px-5
      py-5
    "
  >
    <p className="hiw-step-number">{number}</p>

    <h3 className="hiw-card-title">{title}</h3>

    <p className="hiw-body hiw-card-description">{description}</p>

    <ul className="hiw-card-list">
      {bullets.map((bullet, index) => (
        <li
          key={`${number}-${index}`}
          className="hiw-body hiw-card-list-item"
        >
          <span aria-hidden="true">•</span>
          <span>{bullet}</span>
        </li>
      ))}
    </ul>
  </article>
);

/* -------------------------
   Protection Card
   ------------------------- */

type ProtectionCardProps = {
  icon: ReactNode;
  title: string;
  description: string;
};

const ProtectionCard = ({
  icon,
  title,
  description,
}: ProtectionCardProps) => (
  <article
    className="
      bg-white
      border
      border-black/60
      rounded-[10px]
      px-5
      py-6
      text-center
    "
  >
    <div className="w-8 h-8 mx-auto mb-3 flex items-center justify-center text-black">
      {icon}
    </div>

    <h3 className="hiw-card-title hiw-protection-title">{title}</h3>

    <p className="hiw-body hiw-protection-description">{description}</p>
  </article>
);

/* -------------------------
   FAQ
   ------------------------- */

type FAQProps = {
  q: string;
  a: string;
};

const FAQ = ({ q, a }: FAQProps) => (
  <article className="hiw-faq-item">
    <h3 className="hiw-faq-question">{q}</h3>
    <p className="hiw-faq-answer">{a}</p>
  </article>
);

/* -------------------------
   Page
   ------------------------- */

export default function HowItWorks() {
  useSEO({
    title:
      "How Vastavik Works — Register, Protect & Verify Media Authenticity",
    description:
      "How Vastavik works: upload your media, run AI deepfake and duplicate checks, embed a QR / owner-key watermark, register ownership on the blockchain, then verify any file by scanning its code or entering its Vastavik ID.",
    path: "/howitworks",
    keywords:
      "how Vastavik works, register media authenticity, verify media online, QR watermark protection, blockchain media registration, NFT media verification, how to verify deepfake, media authenticity check",
  });

  return (
    <div className="how-it-works-page min-h-screen bg-white flex flex-col text-black">
      <Header />

      <main className="flex-1 px-5 md:px-8 pt-12 pb-14">
        <div className="w-full max-w-[1080px] mx-auto">
          {/* Page heading */}
          <header className="text-center mb-12">
            <h1 className="hiw-page-title">How it works</h1>

            <p className="hiw-intro">
              Vastavik helps you protect and verify the authenticity of your
              media. Upload your photos, videos, and audio — each file is
              blockchain-verified so you can confidently say:
            </p>

            {/*
            <p className="hiw-intro hiw-intro-highlight">
              If it&apos;s not on Vastavik, don&apos;t trust it.
            </p>
            */}
          </header>

          {/* Get Started */}
          <section className="mb-20">
            <h2 className="hiw-section-title">Get Started</h2>

            <div className="flex flex-col md:flex-row gap-5">
              <StepCard
                number="01"
                title="Create your account"
                description="Sign up with your email and choose a unique Creator ID — your public identifier on the platform."
                bullets={[
                  "Email verification for security",
                  "Choose your unique Creator ID (3-8 chars)",
                  "5 GB free storage on signup",
                ]}
              />

              <StepCard
                number="02"
                title="Upload & protect media"
                description="Upload photos, videos, and audio directly from your device. Every file is registered on a private blockchain with a unique NFT token for tamper-proof records."
                bullets={[
                  "Photos, videos, and audio supported",
                  "Up to 10 files at once (100 MB each)",
                  "Blockchain-verified on every upload",
                ]}
              />

              <StepCard
                number="03"
                title="Choose protection"
                description="Add a VASTAVIK QR code, a Vastavik ID watermark on photos and videos, or generate a Vastavik Voice ID for audio files."
                bullets={[
                  "VASTAVIK QR code overlay",
                  "Vastavik ID watermark",
                  "Vastavik Voice ID for audio",
                ]}
              />
            </div>
          </section>

          {/* Protection options */}
          <section className="mb-20">
            <h2 className="hiw-section-title">Protection options</h2>

            <p className="hiw-body hiw-section-description">
              Choose how you want to protect each file. QR codes and Vastavik ID
              watermarks are available for photos and videos. Audio files get a
              unique Vastavik Voice ID.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <ProtectionCard
                icon={<QrIcon className="w-7 h-7" />}
                title="VASTAVIK QR Code"
                description="A branded QR overlay embedded on your media that links directly to the blockchain verification record."
              />

              <ProtectionCard
                icon={<IdIcon className="w-7 h-7" />}
                title="Vastavik ID"
                description="A visible text watermark of your Vastavik ID on photos and videos to declare authenticity without obscuring the media."
              />

              <ProtectionCard
                icon={<ShieldIcon className="w-7 h-7" />}
                title="NFT"
                description="All media is registered on a private blockchain by default — only you and approved users can access it."
              />

              <ProtectionCard
                icon={<WaveIcon className="w-7 h-7" />}
                title="Vastavik Voice ID"
                description="A short voiceover clip that reads your Vastavik ID, generated for audio files as an authenticity marker."
              />
            </div>
          </section>

          {/* Blockchain verified */}
          <section className="mb-16 text-center">
            <h2 className="hiw-section-title">Blockchain-verified</h2>

            <p className="hiw-body hiw-section-description">
              Every upload is registered on a private Hyperledger Fabric
              blockchain with a unique NFT token — creating an immutable,
              tamper-proof record of authenticity.
            </p>

            <ul
              className="
                hiw-body
                hiw-centered-list
                list-disc
                list-inside
                space-y-1.5
              "
            >
              <li>Upload your photo, video, or audio file</li>
              <li>Choose protection — QR code, watermark, or Voice ID</li>
              <li>File is registered on the blockchain automatically</li>
            </ul>
          </section>

          {/* Verify authenticity */}
          <section className="mb-20 text-center">
            <h2 className="hiw-section-title">Verify authenticity</h2>

            <p className="hiw-body hiw-section-description">
              Anyone can verify your media through the blockchain record using
              the Vastavik ID or by scanning the QR code — no account required
              to verify.
            </p>

            <ul
              className="
                hiw-body
                hiw-centered-list
                list-disc
                list-inside
                space-y-1.5
              "
            >
              <li>Scan the VASTAVIK QR code to verify instantly</li>
              <li>Look up any media using the Vastavik ID</li>
              <li>Public NFT registration coming soon for public records</li>
            </ul>
          </section>

          {/* Quick FAQs */}
          <section className="w-full max-w-[900px] mx-auto">
            <h2 className="hiw-section-title">Quick FAQs</h2>

            <FAQ
              q="What formats are accepted?"
              a="Images: JPEG, PNG, WebP, GIF, HEIC. Videos: MP4, WebM, MOV. Audio: MP3, WAV, M4A, AAC, FLAC, OGG."
            />

            <FAQ
              q="How much free storage do I get?"
              a="New accounts receive 5 GB free storage upon registration."
            />

            <FAQ
              q="Is my media registered on the blockchain?"
              a="Yes — every upload is registered on a private blockchain with a unique NFT token by default. Public NFT registration will be available soon for those who want a publicly accessible record."
            />

            <FAQ
              q="What is a Vastavik Voice ID?"
              a="For audio files, Vastavik generates a short voiceover clip that reads your Vastavik ID — serving as an audio authenticity marker alongside the blockchain record."
            />
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}